import {
  Fragment, type ReactNode
} from "react";
import {
  Dialog, Transition
} from "@headlessui/react";
import {
  XMarkIcon
} from "@heroicons/react/24/outline";

interface PairingModalShellProps {
  isOpen: boolean;
  /** Invoked by both the HeadlessUI Dialog backdrop/escape and the header X button. */
  onClose: () => void;
  title: ReactNode;
  /** Optional aria-label for the close button (Zoho sets "Close"). */
  closeAriaLabel?: string;
  children: ReactNode;
}

/**
 * Shared HeadlessUI Dialog chrome for the OAuth-style pairing modals
 * (Slack, Zoom, Zoho, and the generic OAuth-integration modal). Renders the
 * transition/overlay scaffold, the rounded white panel, and the header
 * (title + close button); the body — including each modal's own error block —
 * is supplied as ``children`` so per-modal placement is preserved.
 */
export function PairingModalShell({
  isOpen,
  onClose,
  title,
  closeAriaLabel,
  children,
}: PairingModalShellProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-[opacity,transform]">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={closeAriaLabel}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
